// Lazy loader for HubSpot form embeds.
//
// The HubSpot embed script (js.hsforms.net/forms/embed/<portalId>.js) initialises
// every .hs-form-frame on the page as soon as it runs, which pulls in Google
// reCAPTCHA — the single biggest script cost on the homepage, where both forms sit
// inside closed modals that most visitors never open. This defers that work until a
// form is actually needed:
//
//   - the frame scrolls into (or near) the viewport
//   - the modal containing the frame is opened
//   - the user hovers/focuses a link that opens that modal (warm-up before the click)
//
// Markup contract: keep the standard HubSpot embed div, drop the <script> tag.
//   <div class="hs-form-frame" data-region="na1" data-form-id="..." data-portal-id="..."></div>

(() => {
  const FRAME_SELECTOR = '.hs-form-frame';
  const ROOT_MARGIN = '400px';

  const requestedPortals = new Set();
  const watchedFrames = new WeakSet();
  let preconnected = false;
  let rescanScheduled = false;

  const idle = (callback) =>
    'requestIdleCallback' in window
      ? window.requestIdleCallback(callback, { timeout: 2000 })
      : setTimeout(callback, 200);

  // Passive triggers wait for the page to finish loading before spending main-thread time.
  function whenSettled(callback) {
    if (document.readyState === 'complete') {
      idle(callback);
    } else {
      window.addEventListener('load', () => idle(callback), { once: true });
    }
  }

  // Warm the connection on intent (hover/focus) so the click doesn't pay for DNS + TLS.
  function preconnect() {
    if (preconnected) return;
    preconnected = true;

    ['https://js.hsforms.net', 'https://forms.hsforms.com'].forEach((href) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      document.head.appendChild(link);
    });
  }

  function loadEmbed(portalId, immediate) {
    if (!portalId || requestedPortals.has(portalId)) return;
    requestedPortals.add(portalId);

    const inject = () => {
      const script = document.createElement('script');
      script.src = `https://js.hsforms.net/forms/embed/${portalId}.js`;
      script.defer = true;
      document.body.appendChild(script);
    };

    // A user-driven trigger (modal open, hover) loads straight away; a passive one
    // (form scrolled into view) waits for the page to settle first.
    if (immediate) {
      preconnect();
      inject();
    } else {
      whenSettled(inject);
    }
  }

  function activate(frame, immediate) {
    loadEmbed(frame.dataset.portalId, immediate);
  }

  // Modal contents are display:none until opened, so IntersectionObserver never fires
  // for them. Watch the modal's own display state instead, plus the links and URL
  // fragments that open it (see modal.js).
  function watchModal(modal, frame) {
    const isOpen = () => modal.style.display === 'block';
    const open = () => activate(frame, true);

    if (isOpen()) {
      open();
      return;
    }

    const observer = new MutationObserver(() => {
      if (!isOpen()) return;
      observer.disconnect();
      open();
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['style', 'class'] });

    const id = modal.id;
    if (!id) return;

    if (window.location.hash === `#${id}`) open();
    window.addEventListener('hashchange', () => {
      if (window.location.hash === `#${id}`) open();
    });

    document.querySelectorAll(`a[href="#${id}"]`).forEach((link) => {
      ['pointerenter', 'focus', 'touchstart'].forEach((event) => {
        link.addEventListener(event, preconnect, { once: true, passive: true });
      });
      link.addEventListener('click', open, { once: true });
    });
  }

  function watchFrame(frame) {
    if (watchedFrames.has(frame)) return;
    watchedFrames.add(frame);

    const modal = frame.closest('.modal');
    if (modal) {
      watchModal(modal, frame);
      return;
    }

    // Anything the user reaches for should load without waiting for idle time.
    ['pointerenter', 'focusin', 'touchstart'].forEach((event) => {
      frame.addEventListener(event, () => activate(frame, true), { once: true, passive: true });
    });

    if (!('IntersectionObserver' in window)) {
      activate(frame, false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          activate(frame, false);
        });
      },
      { rootMargin: ROOT_MARGIN }
    );
    observer.observe(frame);
  }

  function watchAll() {
    document.querySelectorAll(FRAME_SELECTOR).forEach(watchFrame);
  }

  watchAll();

  // Sections rendered after load (theme editor, section rendering API) get picked up
  // too. Rescans are throttled to one idle callback so mutation bursts stay cheap.
  new MutationObserver((mutations) => {
    if (rescanScheduled) return;

    const addedElements = mutations.some((mutation) =>
      Array.prototype.some.call(mutation.addedNodes, (node) => node.nodeType === 1)
    );
    if (!addedElements) return;

    rescanScheduled = true;
    idle(() => {
      rescanScheduled = false;
      watchAll();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
